import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFocusScroll } from "@/lib/navigation";
import { api } from "@/lib/api";
import { FORMAT_OPTIONS, canonical, matchesProduct, normalizeFormat, productTitle } from "@/lib/productNaming";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check, Loader2, RotateCcw, X } from "lucide-react";

const REASONS = [
  ["LARGE_PROJECT", "Large project"],
  ["COMPETITIVE_MATCH", "Competitive match"],
  ["STRATEGIC_CLIENT", "Strategic client"],
  ["CLEARANCE", "Clearance"],
  ["OTHER", "Other"],
];
const fmt$ = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`);
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);

export default function DealerPricing() {
  const [data, setData] = useState(null);
  const [approvals, setApprovals] = useState(null);
  const [edit, setEdit] = useState({});       // variant_id -> {sqft, box, basis}
  const [pendingReason, setPendingReason] = useState({}); // variant_id -> reason (below-floor resubmit)
  const [revise, setRevise] = useState({});   // approval_id -> revised per-sqft
  const [otherNote, setOtherNote] = useState({});     // RC49-PRC-005
  const [confirmFloor, setConfirmFloor] = useState({}); // RC49-PRC-004
  const [params] = useSearchParams();
  const focusSku = params.get("q") || null;  // V5-D pricing navigation
  const [search, setSearch] = useState(focusSku || "");           // RC49-PRC-013
  const [fmtFilter, setFmtFilter] = useState("all");
  const dirtyCount = Object.values(edit).filter((e) => e && e.value).length;
  const guardedSet = (fn) => (v) => {  // RC49-PRC-015: never silently drop unsaved entries
    if (dirtyCount > 0 && !window.confirm(`You have ${dirtyCount} unsaved price entr${dirtyCount > 1 ? "ies" : "y"}. Changing filters keeps them, but continue?`)) return;
    fn(v);
  };
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([
        api.get("/dealer/pricing"),
        api.get("/dealer/pricing/approvals"),
      ]);
      setData(p.data);
      setApprovals(a.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const guardrails = data?.guardrails || {};
  const pendingByVariant = useMemo(() => {
    const m = {};
    (approvals?.approvals || []).forEach((a) => {
      if (a.status === "PENDING_APPROVAL") m[`${a.scope}:${a.variant_id}:${a.project_id || ""}`] = a;
    });
    return m;
  }, [approvals]);

  const save = async (row) => {
    const e = edit[row.variant_id] || {};
    const val = parseFloat(e.value ?? "");
    if (Number.isNaN(val) || val <= 0) {
      toast.error(`Enter a valid customer price as ${e.unit === "box" ? "$/box" : "$/sq ft"}`);
      return;
    }
    const payload = e.unit === "box" ? { resale_per_box: val } : { resale_per_sqft: val };
    if (pendingReason[row.variant_id]) payload.reason = pendingReason[row.variant_id];
    // RC49-PRC-005: Other requires a meaningful explanation before apply/submit
    if (payload.reason === "OTHER") {
      const note = (otherNote[row.variant_id] || "").trim();
      if (note.length < 10) { toast.error("Reason “Other” requires an explanation (at least 10 characters)"); return; }
      payload.note = note;
    }
    if (confirmFloor[row.variant_id]) payload.confirm_below_floor = true;  // RC49-PRC-004
    setBusy(row.variant_id);
    try {
      const { data: res } = await api.put(`/dealer/pricing/${row.variant_id}`, payload);
      if (res.status === "PENDING_APPROVAL") {
        toast.info("Sent for approval — below the margin floor");
      } else if (res.below_floor_applied) {
        toast.success("Below-floor standing price applied and audited");
      } else {
        toast.success("Customer price saved");
      }
      setEdit({ ...edit, [row.variant_id]: {} });
      setPendingReason({ ...pendingReason, [row.variant_id]: "" });
      setConfirmFloor({ ...confirmFloor, [row.variant_id]: false });
      await load();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      if (e.response?.status === 422 && /below the (salesperson )?margin floor/i.test(msg)) {
        toast.warning(msg);
        setPendingReason({ ...pendingReason, [row.variant_id]: pendingReason[row.variant_id] || "OTHER" });
      } else if (e.response?.status === 409) {
        toast.warning(msg);
        setConfirmFloor({ ...confirmFloor, [row.variant_id]: true });
      } else {
        toast.error(msg);
      }
    } finally { setBusy(""); }
  };

  const decide = async (a, action) => {
    setBusy(a.id);
    try {
      const payload = { action, note: "Dealer Pricing UI" };
      if (action === "REVISE") {
        const v = parseFloat(revise[a.id] || "");
        if (Number.isNaN(v)) { toast.error("Enter the revised price per sq ft"); setBusy(""); return; }
        payload.revised_resale_per_sqft = v;
      }
      await api.post(`/dealer/pricing/approvals/${a.id}/decision`, payload);
      toast.success(action === "DECLINE" ? "Request declined" : "Approved — price applied");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const focusHit = !!(focusSku && data && data.rows.some((r) => r.sku === focusSku));
  useFocusScroll(focusHit ? `pricing-row-${focusSku}` : null, focusHit);
  if (!data || !approvals) {
    return <div className="flex min-h-[40vh] items-center justify-center" data-testid="dealer-pricing-loading"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;
  }

  const queue = approvals.approvals.filter((a) => a.status === "PENDING_APPROVAL");
  const canSet = !!data.can_set_standing;  // RC49-PRC-001/002
  const visibleRows = data.rows.filter((r) => (fmtFilter === "all" || normalizeFormat(r.format_in) === fmtFilter) && matchesProduct(search, r));

  return (
    <div data-testid="dealer-pricing-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Commercial</p>
      <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Pricing</h1>
      <p className="mt-2 text-xs text-muted-foreground" data-testid="format-defaults-link">Format-wide defaults (4×4 Zellige, 2×6 Bejmat, 2×2 Mdebdeb) apply to every color unless a product or project price is set. <a href="/dealer/pricing/setup" className="font-semibold text-terracotta hover:underline">Edit format defaults →</a></p>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-warmgrey">
        Your buy prices and customer resale pricing in one place. Whole-box math is authoritative; per-square-foot
        figures are calculated from governed box coverage. Enter either price basis — the other is calculated for you.
        {guardrails.target_margin_pct != null && ` Target margin ${guardrails.target_margin_pct}%.`}
        {" "}<span data-testid="pricing-role-copy">{data.pricing_copy}</span>
      </p>

      {/* RC49-PRC-013/014: alias search + canonical format filter, result count, clear */}
      <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input value={search} onChange={(e) => guardedSet(setSearch)(e.target.value)} placeholder="Search SKU, color code, U.S. or factory color, Mzedej / Bejmat / Mdebdeb, 4×4 / 2×6 / 2×2"
          className="h-10 bg-white sm:max-w-md" data-testid="pricing-search" />
        <select value={fmtFilter} onChange={(e) => guardedSet(setFmtFilter)(e.target.value)} className="h-10 rounded-md border border-border bg-white px-3 text-sm" data-testid="pricing-format-filter">
          {FORMAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span className="text-xs text-warmgrey" data-testid="pricing-result-count">{visibleRows.length} of {data.rows.length} products</span>
        {(search || fmtFilter !== "all") && (
          <button type="button" onClick={() => { setSearch(""); setFmtFilter("all"); }} className="text-xs font-medium text-terracotta hover:underline" data-testid="pricing-clear-filters">Clear filters</button>
        )}
        {dirtyCount > 0 && <span className="text-xs text-status-reserved" data-testid="pricing-unsaved">{dirtyCount} unsaved</span>}
      </div>

      {queue.length > 0 && (
        <div className="mt-6 rounded-md border border-border bg-white p-4" data-testid="pricing-approvals">
          <p className="text-xs font-semibold uppercase tracking-widest text-warmgrey">
            {approvals.can_decide ? "Pricing approvals awaiting your decision" : "Your pricing requests awaiting approval"}
          </p>
          <ul className="mt-3 space-y-3">
            {queue.map((a) => (
              <li key={a.id} className="rounded-md border border-limestone px-4 py-3 text-sm" data-testid={`pricing-approval-${a.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className="font-mono text-xs font-semibold">{a.sku}</span>
                    <span className="ml-2 text-charcoal">{fmt$(a.resale_per_sqft)}/sq ft · {fmt$(a.resale_per_box)}/box</span>
                    <span className="ml-2 text-xs text-warmgrey">margin {fmtPct(a.margin_pct)} · reason: {(REASONS.find(([k]) => k === a.reason) || [null, a.reason])[1]}</span>
                    <p className="mt-1 text-xs text-warmgrey">Requested by {a.requested_by}{a.scope === "project" ? " · project-specific" : ""}</p>
                  </div>
                  {approvals.can_decide && (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button size="sm" onClick={() => decide(a, "APPROVE")} disabled={!!busy} className="bg-terracotta text-white hover:bg-terracotta/90" data-testid={`approval-${a.id}-approve`}>
                        <Check className="mr-1 h-3.5 w-3.5" />Approve
                      </Button>
                      <Input value={revise[a.id] || ""} onChange={(e) => setRevise({ ...revise, [a.id]: e.target.value })}
                        placeholder="Revise $/sq ft" className="h-9 w-32 bg-white" data-testid={`approval-${a.id}-revise-input`} />
                      <Button size="sm" variant="outline" onClick={() => decide(a, "REVISE")} disabled={!!busy} className="border-limestone" data-testid={`approval-${a.id}-revise`}>
                        <RotateCcw className="mr-1 h-3.5 w-3.5" />Revise
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => decide(a, "DECLINE")} disabled={!!busy} className="border-status-sold text-status-sold" data-testid={`approval-${a.id}-decline`}>
                        <X className="mr-1 h-3.5 w-3.5" />Decline
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6 overflow-x-auto rounded-md border border-border bg-white">
        <table className="w-full min-w-[900px] text-sm" data-testid="dealer-pricing-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="sticky left-0 z-10 bg-white px-4 py-3">Product</th><th className="px-4 py-3">Coverage</th>
              <th className="px-4 py-3 text-right">Your price / box</th><th className="px-4 py-3 text-right">Cost / sq ft</th>
              <th className="px-4 py-3">Customer price</th><th className="px-4 py-3 text-right">Profit / box</th>
              <th className="px-4 py-3 text-right">Margin</th><th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-warmgrey" data-testid="pricing-empty">{focusSku && !focusHit ? <span data-testid="pricing-focus-missing">No authorized pricing row matches “{focusSku}”.</span> : "No matching products for the current search and format filter."}</td></tr>
            )}
            {visibleRows.map((r) => {
              const e = edit[r.variant_id] || {};
              const pendKey = `standing:${r.variant_id}:`;
              const pend = pendingByVariant[pendKey];
              return (
                <tr key={r.variant_id} id={`pricing-row-${r.sku}`} className={`border-b border-border align-top ${focusSku === r.sku ? "bg-terracotta/5 ring-2 ring-inset ring-terracotta" : ""}`}
                data-testid={`pricing-row-${r.sku}`} data-focused={focusSku === r.sku ? "true" : undefined}>
                  <td className="sticky left-0 z-10 bg-white px-4 py-3 shadow-[1px_0_0_0_#e7e2da]">
                    <p className="font-semibold text-terracotta">{r.us_color_name || r.color_name}</p>
                    <p className="text-xs text-charcoal" data-testid={`pricing-title-${r.sku}`}>{r.product_title || productTitle(r)}</p>
                    <p className="text-xs text-warmgrey">{r.sku}{r.color_code ? ` · ${r.color_code}` : ""}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-warmgrey whitespace-nowrap">{r.box_coverage_sqft} sq ft/box</td>
                  <td className="px-4 py-3 text-right num text-charcoal">{fmt$(r.buy_price_per_box)}</td>
                  <td className="px-4 py-3 text-right num text-charcoal">{fmt$(r.cost_per_sqft)}</td>
                  <td className="px-4 py-3">
                    {r.customer_price_missing && !e.value && !pend ? (
                      <span className="text-xs font-medium text-status-reserved" data-testid={`pricing-missing-${r.sku}`}>No customer price set</span>
                    ) : null}
                    {!r.customer_price_missing && !pend && (
                      <p className="num text-sm text-charcoal" data-testid={`pricing-current-${r.sku}`}>
                        <span className="whitespace-nowrap font-medium">{fmt$(r.resale_per_sqft)}/sq ft</span> <span className="whitespace-nowrap text-xs text-warmgrey">({fmt$(r.resale_per_box)}/box)</span>
                        <span className="ml-2 badge-status badge-neutral" data-testid={`pricing-source-${r.sku}`}>{r.price_source === "format_default" ? `${r.format_in || "Format"} default` : "Variant price"}</span>
                      </p>
                    )}
                    {pend ? (
                      <span className="text-xs font-medium text-status-reserved" data-testid={`pricing-pending-${r.sku}`}>Pending approval · {fmt$(pend.resale_per_sqft)}/sq ft</span>
                    ) : !canSet ? (
                      <span className="sr-only" data-testid={`pricing-readonly-${r.sku}`}>{r.customer_price_missing ? "—" : `${fmt$(r.resale_per_sqft)}/sq ft`}</span>
                    ) : (
                      <div className="space-y-1.5">
                        {(() => {
                          const unit = e.unit || "sqft";
                          const val = parseFloat(e.value ?? "");
                          const cov = r.box_coverage_sqft || 0;
                          const sq = Number.isNaN(val) ? null : unit === "sqft" ? val : (cov ? val / cov : null);
                          const bx = Number.isNaN(val) ? null : unit === "box" ? val : val * cov;
                          const buy = r.buy_price_per_box || 0;
                          const margin = bx && bx > 0 ? ((bx - buy) / bx) * 100 : null;
                          return (
                            <>
                              <div className="flex flex-wrap items-center gap-2">
                                <div className="flex rounded-md border border-limestone p-0.5" data-testid={`pricing-unit-${r.sku}`}>
                                  {[["sqft", "$/sq ft"], ["box", "$/box"]].map(([u, l]) => (
                                    <button key={u} type="button" onClick={() => setEdit({ ...edit, [r.variant_id]: { ...e, unit: u, basis: u } })}
                                      className={`rounded px-2 py-1 text-[11px] font-semibold ${unit === u ? "bg-terracotta text-white" : "text-charcoal"}`} data-testid={`pricing-unit-${u}-${r.sku}`}>{l}</button>
                                  ))}
                                </div>
                                <Input value={e.value ?? ""} inputMode="decimal" onChange={(ev) => setEdit({ ...edit, [r.variant_id]: { ...e, value: ev.target.value, unit, basis: unit, [unit]: ev.target.value } })}
                                  placeholder={unit === "sqft" ? "$ per sq ft" : "$ per box"} className="h-9 w-28 bg-white" data-testid={`pricing-input-${r.sku}`} />
                              </div>
                              <p className="text-[11px] text-warmgrey" data-testid={`pricing-preview-${r.sku}`}>
                                {sq != null && bx != null && !Number.isNaN(sq) ? <>Will save as <span className="whitespace-nowrap font-medium text-charcoal">{fmt$(sq)}/sq ft</span> (<span className="whitespace-nowrap">{fmt$(bx)}/box</span>) · est. margin <span className="font-medium text-charcoal">{margin == null ? "—" : fmtPct(margin)}</span></> : <>Enter a price as <strong>{unit === "sqft" ? "$/sq ft" : "$/box"}</strong> — {cov} sq ft/box governed coverage</>}
                              </p>
                            </>
                          );
                        })()}
                        {pendingReason[r.variant_id] !== undefined && pendingReason[r.variant_id] !== "" && (
                          <Select value={pendingReason[r.variant_id]} onValueChange={(v) => setPendingReason({ ...pendingReason, [r.variant_id]: v })}>
                            <SelectTrigger className="h-9 w-56 bg-white" data-testid={`pricing-reason-${r.sku}`}><SelectValue placeholder="Reason for exception" /></SelectTrigger>
                            <SelectContent className="bg-white">
                              {REASONS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`pricing-reason-${r.sku}-${k.toLowerCase()}`}>{l}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                        {pendingReason[r.variant_id] === "OTHER" && (
                          <Input value={otherNote[r.variant_id] || ""} onChange={(ev) => setOtherNote({ ...otherNote, [r.variant_id]: ev.target.value })}
                            placeholder="Explain the reason (required)" className="h-9 w-72 bg-white" data-testid={`pricing-other-note-${r.sku}`} />
                        )}
                        {confirmFloor[r.variant_id] && (
                          <p className="text-[11px] text-status-sold" data-testid={`pricing-confirm-floor-${r.sku}`}>Below the margin floor — click again to confirm; this change is applied and audited.</p>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right num text-charcoal" data-testid={`pricing-gp-${r.sku}`}>{r.customer_price_missing ? "—" : fmt$(r.gross_profit_per_box)}</td>
                  <td className="px-4 py-3 text-right" data-testid={`pricing-margin-${r.sku}`}>
                    <span className={`num ${r.needs_approval ? "font-semibold text-status-sold" : "text-charcoal"}`}>
                      {r.customer_price_missing ? "—" : fmtPct(r.margin_pct)}
                    </span>
                    {r.needs_approval && <p className="text-[10px] text-status-sold">below floor</p>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!pend && canSet && (
                      <Button size="sm" variant="outline" onClick={() => save(r)} disabled={!!busy}
                        className="border-terracotta text-terracotta hover:bg-terracotta hover:text-white" data-testid={`pricing-save-${r.sku}`}>
                        {busy === r.variant_id && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                        {r.customer_price_missing ? "Set customer price" : "Update price"}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
